import { useEffect } from "react";
import { useContent } from "@/lib/content";

/** Injects Google Analytics, GTM, Meta Pixel, and custom head/body HTML
 *  based on the analytics KV doc. Idempotent — won't double-inject. */
export default function AnalyticsLoader() {
  const { analytics } = useContent();

  useEffect(() => {
    if (!analytics) return;
    const { ga4_id, gtm_id, meta_pixel_id, tiktok_pixel_id, linkedin_partner_id, hotjar_id, clarity_id, custom_head_html, custom_body_html } = analytics;

    // ---- GA4 ----
    if (ga4_id && !document.getElementById("ga4-script")) {
      const s = document.createElement("script");
      s.id = "ga4-script"; s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4_id}`;
      document.head.appendChild(s);
      const inline = document.createElement("script");
      inline.id = "ga4-inline";
      inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4_id}');`;
      document.head.appendChild(inline);
    }

    // ---- GTM ----
    if (gtm_id && !document.getElementById("gtm-script")) {
      const s = document.createElement("script");
      s.id = "gtm-script";
      s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtm_id}');`;
      document.head.appendChild(s);
    }

    // ---- Meta Pixel ----
    if (meta_pixel_id && !document.getElementById("meta-pixel")) {
      const s = document.createElement("script");
      s.id = "meta-pixel";
      s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${meta_pixel_id}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }

    // ---- TikTok Pixel ----
    if (tiktok_pixel_id && !document.getElementById("tt-pixel")) {
      const s = document.createElement("script");
      s.id = "tt-pixel";
      s.text = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src=i+"?sdkid="+e+"&lib="+t;var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(a,s)};ttq.load('${tiktok_pixel_id}');ttq.page();}(window,document,'ttq');`;
      document.head.appendChild(s);
    }

    // ---- LinkedIn Insight ----
    if (linkedin_partner_id && !document.getElementById("li-partner")) {
      const s = document.createElement("script");
      s.id = "li-partner";
      s.text = `_linkedin_partner_id="${linkedin_partner_id}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s);})(window.lintrk);`;
      document.head.appendChild(s);
    }

    // ---- Hotjar ----
    if (hotjar_id && !document.getElementById("hj-script")) {
      const s = document.createElement("script");
      s.id = "hj-script";
      s.text = `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${hotjar_id},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
      document.head.appendChild(s);
    }

    // ---- Microsoft Clarity ----
    if (clarity_id && !document.getElementById("clarity-script")) {
      const s = document.createElement("script");
      s.id = "clarity-script";
      s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${clarity_id}");`;
      document.head.appendChild(s);
    }

    // ---- Custom HTML in <head> ----
    // Admin-only content from CMS (analytics KV) — intentionally allows raw <script>/<meta> tags
    // for GA/GTM/pixels. Parsed via DOMParser (not element.innerHTML) to avoid direct innerHTML assignment.
    if (custom_head_html && !document.getElementById("custom-head")) {
      const marker = document.createElement("meta");
      marker.id = "custom-head";
      marker.name = "places-custom-head";
      document.head.appendChild(marker);
      const parsed = new DOMParser().parseFromString(`<head>${custom_head_html}</head>`, "text/html");
      [...parsed.head.children].forEach((node) => document.head.appendChild(document.importNode(node, true)));
    }

    // ---- Custom HTML at end of <body> ----
    // Admin-only content from CMS — same rationale as custom_head_html above.
    if (custom_body_html && !document.getElementById("custom-body")) {
      const marker = document.createElement("meta");
      marker.id = "custom-body";
      marker.name = "places-custom-body";
      document.head.appendChild(marker);
      const parsed = new DOMParser().parseFromString(`<body>${custom_body_html}</body>`, "text/html");
      [...parsed.body.children].forEach((node) => document.body.appendChild(document.importNode(node, true)));
    }
  }, [analytics]);

  return null;
}

/** Helper to fire conversion events from anywhere in the app. */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("event", name, params);
  if (window.fbq) window.fbq("track", name, params);
  if (window.dataLayer) window.dataLayer.push({ event: name, ...params });
}
